import React, {useEffect, useRef, useState} from "react";
import {useChat} from "./ChatContext";
import Toggle from "../../../components/Toggle";
import Button from "../../../components/Button";
import PlayerList from "../players/PlayerList";
import {usePlayers} from "../players/PlayerContext";
import {ChatChannel, ChatMessage} from "../interfaces";
import {useLocalStorageT} from "../../../hooks/useLocalStorage";
import {rpc_chatInit, rpc_sendChatMessage} from "../rpc";
import {useSocket} from "../SocketContext";
import {useOverlay} from "../overlay/OverlayContext";

export default function Chat(){

    const socket = useSocket()
    const players = usePlayers()

    const [showTimestamps, setShowTimestamps] = useLocalStorageT<boolean>("chatShowTimestamps")
    const [zebraMode, setZebraMode] = useLocalStorageT<boolean>("chatZebraMode")

    const [filteredChat, setFilteredChat] = useState<ChatMessage[]>()

    const [chatFilter, setChatFilter] = useState<{steamId?: string, teamId?: number, channel?: ChatChannel, [key: string]: any}>({})

    useEffect(() => {
        if (!socket.isConnected){
            return
        }

        rpc_chatInit(socket, 1000, chatFilter.steamId ?? "0", chatFilter.teamId ?? 0, chatFilter.channel ?? -1).then(r => {
            if (r){
                setFilteredChat(r.messages)
            }
        })
    }, [socket, chatFilter]);

    return (
        <div className="size-full flex flex-row p-4 relative gap-4">

            <div className="flex flex-col flex-grow flex-shrink">
                <div className="border-neutral-500 mb-4">
                    <h1 className="text-2xl font-bold text-white mb-2 flex">Chat
                        {chatFilter.teamId && <> (Team {chatFilter.teamId})</>}
                        {chatFilter.steamId && <> (Player {chatFilter.steamId})</>}
                        {(chatFilter.steamId || chatFilter.teamId) &&
                            <button className="self-center underline text-primary text-sm font-normal ms-2"
                                 onClick={() => setChatFilter({})}>Clear filter</button>}
                    </h1>
                    <div className="flex flex-row flex-wrap gap-4">
                        <div className="flex flex-row gap-2 items-center">
                            <div className="flex flex-row gap-2 bg-neutral-600 p-1 rounded-md">
                                <ChannelFilterButton text="ALL CHANNELS" channel={undefined}/>
                                <ChannelFilterButton text="GLOBAL" channel={ChatChannel.GLOBAL}/>
                                <ChannelFilterButton text="TEAM" channel={ChatChannel.TEAM}/>
                            </div>
                        </div>
                        <Toggle label="Show Timestamps" checked={showTimestamps!} onChange={() => setShowTimestamps(!showTimestamps)} />
                        <Toggle label="Alternating Colors" checked={zebraMode!} onChange={() => setZebraMode(!zebraMode)} />
                    </div>
                </div>

                <ChatWindow overrideMessages={filteredChat} />
            </div>

            <div className="bg-neutral-800 p-4 rounded-md w-80 flex flex-col flex-shrink-0">
                <h4 className="font-bold text-lg">Online Players ({players.online.length})</h4>
                <p className="text-sm mb-2 text-neutral-400" >Click on a player to view their chat messages.</p>
                <PlayerList onPlayerClick={s => setChatFilter({steamId: s})} rowMapper={p => {
                    return <>
                        {p.displayName}
                        {(!!p.teamId && p.teamId > 0) && <button className="float-right rounded-md bg-primary px-2"
                         onClick={() => setChatFilter({teamId: p.teamId})}>TEAM</button>}
                    </>
                }} />
            </div>

        </div>
    )

    function ChannelFilterButton(props: {channel?: ChatChannel, text: string}){
        return <button onClick={() => setChatFilter(f => {
            f.channel = props.channel
            return { ...f }
        })}
            className={`rounded-md px-2 ${chatFilter.channel === props.channel ? "bg-primary" : "bg-neutral-600"}`} >
            {props.text}</button>
    }
}

export function ChatWindow(props: {overrideMessages?: ChatMessage[], hideInput?: boolean}){

    const chat = useChat()
    const socket = useSocket()
    const overlay = useOverlay()

    const messages = props.overrideMessages ?? chat.messages.value
    const [inputMessage, setInputMessage] = useState("")

    const chatRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const [lockScroll, setLockScroll] = useState(false)

    const [showTimestamps, ] = useLocalStorageT<boolean>("chatShowTimestamps")
    const [zebraMode, ] = useLocalStorageT<boolean>("chatZebraMode")

    function handleScroll(e: React.UIEvent<HTMLDivElement>) {
        const { offsetHeight, scrollHeight, scrollTop } = e.currentTarget;
        const shouldLock = scrollHeight > scrollTop + offsetHeight + 100;
        if (lockScroll !== shouldLock) {
            setLockScroll(shouldLock)
        }
    }

    useEffect(() => {
        if (!lockScroll){
            scrollDown();
        }
    }, [messages, lockScroll]);

    function scrollDown(){
        if (chatRef.current){
            chatRef.current.scrollTo(0, chatRef.current.scrollHeight);
        }
    }

    function getTimestamp(seconds: number){
        const date = new Date(seconds * 1000);
        return date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    }

    function getChannelName(channel: ChatChannel): string {
        switch(channel) {
            case ChatChannel.GLOBAL: return "GLOBAL";
            case ChatChannel.TEAM: return "TEAM";
            case ChatChannel.SERVER: return "SERVER";
            case ChatChannel.CARDS: return "CARDS";
            case ChatChannel.LOCAL: return "LOCAL";
            case ChatChannel.CLAN: return "CLAN";
            case ChatChannel.EXTERNAL_DM: return "EXT_DM";
            case ChatChannel.SYSTEM: return "SYSTEM";
            default: return "UNKNOWN";
        }
    }

    async function sendChat(message: string){
        console.log("sendChat", message)
        await rpc_sendChatMessage(socket, message, "0")
    }

    async function handleSendMessage() {
        if (inputMessage.trim()) {
            await sendChat(inputMessage.trim())
            setInputMessage("")
            // Focus back on input after sending
            inputRef.current?.focus()
        }
    }

    function handleKeyPress(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'Enter') {
            e.preventDefault()
            handleSendMessage()
        }
    }

    return (
        <div className="flex flex-col flex-grow overflow-auto size-full relative">
            <div
                ref={chatRef} 
                onScroll={handleScroll}
                className="bg-black text-white font-mono text-sm p-4 relative sm:rounded-md size-full overflow-y-auto
                [&::-webkit-scrollbar]:w-3 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent
                [&::-webkit-scrollbar-thumb]:rounded-sm [&::-webkit-scrollbar-thumb]:bg-green-500 flex flex-col"
            >
                <div style={{ whiteSpaceCollapse: "preserve", lineBreak: 'anywhere'}}>
                    {messages.map((msg, index) => (
                        <div className={`flex gap-2 md:gap-3 ${zebraMode && "even:bg-neutral-700"}`} key={index}>
                            {showTimestamps && <span className="flex-shrink-0 text-neutral-400">{getTimestamp(msg.timestamp)}</span>}
                            <span className="flex-shrink-0 text-blue-400 whitespace-pre">{`[${getChannelName(msg.channel)}]`.padStart(8)}</span>
                            <div>
                                {msg.channel !== ChatChannel.SERVER && <button disabled={msg.authorSteamId === "0"}
                                         onClick={() => overlay.openPlayerOverlay(msg.authorSteamId)}
                                         className="self-start flex-shrink-0 text-green-400 hover:underline disabled:hover:no-underline me-2">{msg.authorDisplayName}:</button>}
                                <span className="text-white">{msg.message}</span>
                            </div>
                        </div>
                    ))}
                </div>
                {lockScroll && (
                    <div className="z-20 sticky flex items-center justify-center bottom-4">
                        <button onClick={scrollDown}
                                className="px-2 py-1 rounded-md bg-primary hover:opacity-90">Scroll to bottom
                        </button>
                    </div>
                )}
                <div className="flex-grow"></div>
            </div>
            
            {/* Chat Input */}
            {!props.hideInput && <div className="flex flex-row gap-2 p-4 bg-neutral-800 rounded-md mt-2">

                <input
                    ref={inputRef}
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type a message..."
                    className="flex-grow px-3 py-2 bg-neutral-700 text-white rounded-md border border-neutral-600
                                 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent
                                 placeholder-neutral-400"
                />
                <Button
                    text="Send"
                    onClick={handleSendMessage}
                    disabled={!inputMessage.trim()}
                    className="px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed bg-primary hover:bg-primary-hover"
                />
            </div>}
        </div>
    )
}